import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  MenuItem,
  Alert,
  Typography,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const DebtorsEdit = ({ open, onClose, editData, token, onSuccess }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/customer-debtors",
    token
  );

  const { data: shopsData, isLoading: shopLoading } = useFetchObjects(
    ["shop", "debtor-edit"],
    "shop/shop",
    token
  );
  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];
  const [selectedShop, setSelectedShop] = useState("");

  const { data: customersData, loading: customersLoading } = useFetchObjects(
    ["customers", "debtor-edit", selectedShop],
    selectedShop ? `shop/customers?page_size=all&shop=${selectedShop}` : "",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency", "debtor-edit", selectedShop],
    selectedShop ? `shop/currencies?page_size=all&shop=${selectedShop}` : "",
    token
  );

  const customers = customersData?.results || customersData || [];
  const currencies = getUniqueCurrencies(
    currenciesData?.results || currenciesData || []
  );

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [loanDate, setLoanDate] = useState("");
  const [notes, setNotes] = useState("");
  const initialRateSelectionRef = useRef("");

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "debtor-edit", selectedShop],
    selectedShop ? `shop/accounts/?page_size=all&shop=${selectedShop}` : "",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];

  const selectedCurrencyId = selectedCurrency?.id
    ? String(selectedCurrency.id)
    : "";
  const shopAccounts = useMemo(() => accounts, [accounts]);
  const selectedAccount = useMemo(
    () =>
      shopAccounts.find((a) => String(a.id) === String(selectedAccountId)) ||
      null,
    [shopAccounts, selectedAccountId]
  );

  useEffect(() => {
    if (editData) {
      setAmount(editData.amount ?? "");
      setExchangeRate(editData.exchange_rate || "");
      setLoanDate(editData.loan_date ? editData.loan_date.slice(0, 16) : "");
      setNotes(editData.notes || "");
      if (editData.shop) setSelectedShop(String(editData.shop));
      if (editData.customer_details) {
        setSelectedCustomer({
          id: editData.customer_details.id,
          name: editData.customer_details.name,
        });
      } else if (editData.customer) {
        const c = customers.find((x) => x.id === editData.customer);
        if (c) setSelectedCustomer(c);
      }
      if (editData.currency_details) {
        setSelectedCurrency({
          id: editData.currency_details.id,
          title: editData.currency_details.title,
        });
      } else if (editData.currency && currencies.length) {
        const curr = currencies.find((x) => x.id === editData.currency);
        if (curr) setSelectedCurrency(curr);
      }
      const accountId = editData.account_details?.id
        ? String(editData.account_details.id)
        : editData.account
          ? String(editData.account)
          : "";
      setSelectedAccountId(accountId);
      const currencyId = editData.currency_details?.id
        ? String(editData.currency_details.id)
        : editData.currency
          ? String(editData.currency)
          : "";
      initialRateSelectionRef.current = `${currencyId}:${accountId}`;
    }
  }, [editData, customers, currencies]);

  useEffect(() => {
    if (accountLoading || !selectedAccountId) return;
    if (shopAccounts.length === 0) return;
    const exists = shopAccounts.some(
      (a) => String(a.id) === String(selectedAccountId)
    );
    if (!exists) {
      setSelectedAccountId("");
    }
  }, [accountLoading, shopAccounts, selectedAccountId]);

  const { data: directRateData } = useFetchObjects(
    [
      "debtor-edit-rate-direct",
      selectedShop,
      selectedCurrencyId,
      selectedAccount?.currency,
    ],
    selectedShop && selectedCurrencyId && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShop}&from_currency=${selectedCurrencyId}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    [
      "debtor-edit-rate-reverse",
      selectedShop,
      selectedCurrencyId,
      selectedAccount?.currency,
    ],
    selectedShop && selectedCurrencyId && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShop}&from_currency=${selectedAccount.currency}&to_currency=${selectedCurrencyId}`
      : "",
    token
  );

  const exchangeRatePreview = useMemo(() => {
    if (!selectedCurrencyId) return "";
    const accountCurrencyId = selectedAccount?.currency ?? selectedCurrencyId;
    if (String(selectedCurrencyId) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [selectedCurrencyId, selectedAccount, directRateData, reverseRateData]);

  const convertedAmountPreview = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (
      !Number.isFinite(nAmount) ||
      !Number.isFinite(nRate) ||
      nAmount <= 0 ||
      nRate <= 0
    ) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRate]);

  useEffect(() => {
    if (!editData || !exchangeRatePreview) return;
    const currentRateSelection = `${selectedCurrencyId}:${selectedAccountId}`;
    if (currentRateSelection !== initialRateSelectionRef.current) {
      setExchangeRate(exchangeRatePreview);
    }
  }, [editData, exchangeRatePreview, selectedCurrencyId, selectedAccountId]);

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.();
      onClose();
    }
  }, [isSuccess, onClose, onSuccess]);

  const handleSubmit = () => {
    if (
      !editData?.id ||
      !selectedShop ||
      !selectedCustomer?.id ||
      !selectedCurrency?.id ||
      !amount
    )
      return;
    if (selectedAccountId && !exchangeRate) return;
    handleUpdate(editData.id, {
      customer: selectedCustomer.id,
      currency: selectedCurrency.id,
      account: selectedAccountId || null,
      amount: Number(amount),
      exchange_rate: selectedAccountId ? exchangeRate : exchangeRate || 1,
      loan_date: loanDate,
      notes: notes.trim() || undefined,
    });
  };

  const settledAmount = Number(editData?.settled_amount || 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("debtor.editTitle", "Edit debtor")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {settledAmount > 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {t("debtor.settledInfo", "Already settled")}: {settledAmount}
            </Alert>
          )}

          <TextField
            select
            margin="dense"
            label={t("debtor.selectShop", "Select branch")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => {
              setSelectedShop(e.target.value);
              setSelectedCustomer(null);
              setSelectedCurrency(null);
              setSelectedAccountId("");
            }}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              shops.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <Autocomplete
            value={selectedCustomer}
            onChange={(e, newValue) => setSelectedCustomer(newValue)}
            options={selectedShop ? customers || [] : []}
            getOptionLabel={(option) => option?.name ?? "—"}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={customersLoading}
            disabled={!selectedShop}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("debtor.customer", "Customer")}
                margin="dense"
                fullWidth
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {customersLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Autocomplete
            value={selectedCurrency}
            onChange={(e, newValue) => {
              setSelectedCurrency(newValue);
              setSelectedAccountId("");
            }}
            options={selectedShop ? currencies || [] : []}
            getOptionLabel={(option) => option?.title ?? ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={currencyLoading}
            disabled={!selectedShop}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("debtor.selectCurrency", "Currency")}
                margin="dense"
                fullWidth
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {currencyLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            select
            margin="dense"
            label={t("debtor.account", "Account")}
            fullWidth
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={!selectedShop || !selectedCurrencyId || accountLoading}
            helperText={t(
              "debtor.accountOptionalHint",
              "Optional. Select an account to deduct from it; leave empty to only increase customer balance."
            )}
          >
            <MenuItem value="">
              {t("debtor.noAccountSelected", "None (do not deduct from account)")}
            </MenuItem>
            {accountLoading ? (
              <MenuItem value="__loading" disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : shopAccounts.length === 0 ? (
              <MenuItem value="" disabled>
                {t("debtor.noAccount", "No accounts found")}
              </MenuItem>
            ) : (
              shopAccounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("debtor.amount", "Amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: settledAmount || 0, step: 0.01 }}
            helperText={
              settledAmount > 0
                ? t(
                    "debtor.minAmountHint",
                    "Cannot be less than settled amount"
                  )
                : undefined
            }
          />

          {selectedAccountId && (
            <>
              <TextField
                margin="dense"
                label={t("debtor.exchangeRate", "Exchange Rate")}
                type="number"
                fullWidth
                required
                value={exchangeRate}
                onChange={(e) => setExchangeRate(e.target.value)}
                inputProps={{ min: 0, step: "0.000001" }}
              />

              <TextField
                margin="dense"
                label={t("debtor.convertedAmount", "Converted Amount")}
                fullWidth
                value={convertedAmountPreview}
                disabled
              />
            </>
          )}

          <TextField
            margin="dense"
            label={t("debtor.loanDate", "Date")}
            type="datetime-local"
            fullWidth
            required
            value={loanDate}
            onChange={(e) => setLoanDate(e.target.value)}
          />

          <TextField
            margin="dense"
            label={t("debtor.notes", "Notes")}
            multiline
            rows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {t(
              "debtor.editHint",
              "If an account is selected, updating will refresh the account debit. If none is selected, no account deduction is made."
            )}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("debtor.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedShop ||
            !selectedCustomer ||
            !selectedCurrency ||
            !amount ||
            (Boolean(selectedAccountId) && !exchangeRate)
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("debtor.save", "Save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DebtorsEdit;
